import type { RosterSettings, ScholarshipPlayer } from '../../../../types/scholarships';
import { groupPitchersByRole, summaryName } from './pitchingStaffSummary';
import { HandednessName } from './RosterPlayerMarks';

interface Props {
  /** LHP then RHP, each in staff order. */
  staff: ScholarshipPlayer[];
  settings: RosterSettings;
  /** What the board calls its settings dialog, for the empty-state hint. */
  configureLabel?: string;
}

/**
 * Roles as columns, each pitcher listed under the role they carry. Read-only: the role is set on
 * the pitcher's row above, and this shows where the staff is thin.
 */
export const PitchingRoleSummary = ({ staff, settings, configureLabel = 'Configure roster' }: Props) => {
  const columns = groupPitchersByRole(staff, settings.pitchingRoles);
  const unassigned = staff.filter((p) => !p.pitchingRole?.trim()).length;

  return (
    <section className="pitching-roles" aria-labelledby="pitching-roles-heading">
      <div className="pitching-roles__header">
        <h2 id="pitching-roles-heading" className="pitching-roles__title">
          Roles
        </h2>
        <p className="pitching-roles__hint">
          Set a role on a pitcher's row above.
          {unassigned > 0 && ` ${unassigned} without a role.`}
        </p>
      </div>
      {columns.length === 0 ? (
        <p className="pitching-roles__empty">
          No pitching roles set up. Add them in {configureLabel}.
        </p>
      ) : (
        <ul className="pitching-roles__grid">
          {columns.map(({ role, pitchers }) => (
            <li key={role} className="pitching-roles__column">
              <h3 className="pitching-roles__role">{role}</h3>
              {pitchers.length === 0 ? (
                <p className="pitching-roles__none">None</p>
              ) : (
                <ol className="pitching-roles__pitchers">
                  {pitchers.map((p) => (
                    <li key={p.id} className="pitching-roles__pitcher">
                      <HandednessName
                        name={summaryName(p)}
                        bats={p.bats}
                        settings={settings}
                      />
                      <span className="roster-sr-only">, {p.position}</span>
                    </li>
                  ))}
                </ol>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
